import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Share2, RefreshCw, Shield, Lock, Mail, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreviewPage() {
  useSEO({ title: "Preview Your Card", description: "Your AI-generated sports trading card is ready to preview. Download the full HD version for $29.", path: "/preview" });
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [email, setEmail] = useState("");
  const [includesPrint, setIncludesPrint] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [accountPromptDismissed, setAccountPromptDismissed] = useState(false);
  const { user, token } = useAuth();

  // Shipping address state
  const [shippingName, setShippingName] = useState("");
  const [shippingLine1, setShippingLine1] = useState("");
  const [shippingLine2, setShippingLine2] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingCountry, setShippingCountry] = useState("US");

  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountApplied, setDiscountApplied] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);

  const basePrice = includesPrint ? 42 : 17;
  const finalPrice = discountPercent > 0 ? (basePrice * (1 - discountPercent / 100)).toFixed(2) : basePrice.toFixed(2);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    try {
      const resp = await axios.post(`${API}/validate-discount`, { code: discountCode });
      setDiscountPercent(resp.data.discount_percent);
      setDiscountApplied(resp.data.code);
      toast.success(`${resp.data.discount_percent}% discount applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid discount code");
      setDiscountPercent(0);
      setDiscountApplied("");
    } finally { setDiscountLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/cards/${cardId}`);
        setCard(resp.data);
        // Facebook Pixel: ViewContent — card preview displayed
        if (window.fbq) window.fbq('track', 'ViewContent');
      } catch {
        toast.error("Card not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [cardId]);

  const handleCheckout = async () => {
    // GA4 + FB Pixel on first click
    if (!checkoutLoading) {
      if (window.gtag) window.gtag('event', 'checkout_started', { card_id: cardId });
      if (window.fbq) window.fbq('track', 'InitiateCheckout');
    }

    // Validate email
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate shipping if print selected
    if (includesPrint) {
      if (!shippingName.trim() || !shippingLine1.trim() || !shippingCity.trim() || !shippingState.trim() || !shippingZip.trim() || !shippingCountry.trim()) {
        toast.error("Please complete all required shipping fields");
        return;
      }
    }

    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const payload = {
        card_id: cardId,
        email: email,
        includes_print: includesPrint,
        origin_url: originUrl,
        discount_code: discountApplied || null,
      };
      if (includesPrint) {
        payload.shipping_name = shippingName.trim();
        payload.shipping_address_line1 = shippingLine1.trim();
        payload.shipping_address_line2 = shippingLine2.trim() || null;
        payload.shipping_city = shippingCity.trim();
        payload.shipping_state = shippingState.trim();
        payload.shipping_zip = shippingZip.trim();
        payload.shipping_country = shippingCountry.trim();
      }
      const resp = await axios.post(`${API}/checkout/create`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });

      // Redirect to Stripe Checkout
      if (resp.data.url) {
        window.location.href = resp.data.url;
      } else {
        toast.error("Failed to create checkout session");
        setCheckoutLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleRegenerate = async () => {
    // Store original image URL before first regeneration
    if (!originalImageUrl && card) {
      setOriginalImageUrl(card.preview_image_url || card.full_image_url);
    }
    setRegenerating(true);
    setShowingOriginal(false);
    try {
      await axios.post(`${API}/cards/${cardId}/regenerate`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      navigate(`/generating/${cardId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regeneration failed");
      setRegenerating(false);
    }
  };

  const handleGoBackToOriginal = () => {
    if (originalImageUrl && card) {
      setCard({ ...card, preview_image_url: originalImageUrl });
      setShowingOriginal(true);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const text = card ? `I turned my dog ${card.dog_name} into a ${card.sport} legend!` : "Check out my dog's sports card!";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Card My Dog", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]">
        <div className="w-10 h-10 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]">
        <div className="text-center">
          <h2 className="font-heading text-3xl uppercase text-[var(--color-text-dark)] mb-4">Card not found</h2>
          <Link to="/create" className="font-body text-[var(--color-gold)]">Create a new card</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16" data-testid="preview-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Card display — admin sees full-res unwatermarked */}
          <div className="flex justify-center">
            <div className="trading-card-frame card-tilt w-full max-w-[360px] relative" data-testid="preview-card">
              {(card.full_image_url || card.preview_image_url) ? (
                <>
                  <img
                    src={card.full_image_url || card.preview_image_url}
                    alt={`${card.dog_name} trading card`}
                    className="w-full aspect-[2/3] object-cover"
                  />
                  {/* Clean centered watermark — preview only, not shown for admin */}
                  {!card.full_image_url && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="preview-watermark">
                      <span className="font-heading text-lg sm:text-xl tracking-widest uppercase select-none" style={{ transform: "rotate(-30deg)", color: "rgba(255,255,255,0.3)" }}>
                        CardMyDog.com
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full aspect-[3/4] bg-[var(--color-cream)] flex items-center justify-center">
                  <p className="font-body text-[var(--color-text-light)]">Preview generating...</p>
                </div>
              )}
            </div>
          </div>

          {/* Details + CTAs */}
          <div className="flex flex-col justify-center">
            <div className="font-accent text-xs tracking-widest uppercase mb-2" style={{ color: "var(--color-gold)" }}>
              Preview Ready
            </div>
            {card.full_image_url && (
              <div className="mb-3 flex items-center gap-2 px-3 py-1.5 rounded-[8px] w-fit" style={{ backgroundColor: "var(--color-navy)" }}>
                <Lock size={12} style={{ color: "var(--color-gold)" }} />
                <span className="font-accent text-[10px] tracking-widest uppercase text-white">Admin &mdash; Full Resolution &mdash; No Watermark</span>
              </div>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-[var(--color-text-dark)] mb-2" data-testid="preview-dog-name">
              {card.dog_name}
            </h1>
            <p className="font-body text-[var(--color-text-mid)] mb-6 capitalize">
              {card.sport} &middot; {card.position} &middot; {card.card_style} style
            </p>

            {/* Pricing + print checkbox — always visible */}
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-4 mb-4" data-testid="price-breakdown">
              <div className="flex items-center justify-between mb-3">
                <span className="font-body text-sm text-[var(--color-text-dark)]">Digital download</span>
                <span className="font-body text-sm">
                  <span className="line-through text-[var(--color-text-light)] mr-2">$29</span>
                  <span className="font-heading text-2xl font-bold" style={{ color: "var(--color-gold)" }}>$17</span>
                  <span className="font-accent text-[10px] ml-1.5 px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(245,166,35,0.12)", color: "var(--color-gold)" }}>Launch Special</span>
                </span>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-[8px] bg-[var(--color-cream)] border border-[var(--color-border)]">
                <Checkbox
                  id="print-upsell"
                  checked={includesPrint}
                  onCheckedChange={(checked) => setIncludesPrint(!!checked)}
                  className="mt-0.5"
                  data-testid="print-upsell-checkbox"
                />
                <label htmlFor="print-upsell" className="cursor-pointer">
                  <span className="font-body text-sm font-medium text-[var(--color-text-dark)] block">
                    Add Physical Print +$25
                  </span>
                  <span className="font-body text-xs text-[var(--color-text-mid)]">
                    Professional 8x10 matte art print, ready to frame. Ships flat in a protective mailer in 5-7 days. Makes the perfect gift.
                  </span>
                </label>
              </div>
            </div>

            {/* Primary CTA */}
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="w-full font-heading tracking-widest uppercase text-lg py-4 rounded-[8px] transition-colors mb-4 disabled:opacity-60"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="download-cta"
            >
              {checkoutLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-[var(--color-navy)] border-t-transparent rounded-full animate-spin" />
                  Redirecting to checkout...
                </span>
              ) : includesPrint ? (
                <>Proceed to Checkout &mdash; ${finalPrice}</>
              ) : (
                <><Download size={18} className="inline mr-2" />Download Your Card &mdash; ${finalPrice}</>
              )}
            </button>

            {/* Regenerate link — understated, below CTA */}
            <p className="text-center mb-4">
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="font-body text-xs text-[var(--color-text-light)] hover:text-[var(--color-gold)] transition-colors disabled:opacity-40"
                data-testid="regenerate-link"
              >
                {regenerating ? "Regenerating..." : "Not happy with it? Regenerate free \u2192"}
              </button>
              {(card.regeneration_count >= 1 || originalImageUrl) && !showingOriginal && (
                <><br />
                <button
                  onClick={handleGoBackToOriginal}
                  className="font-body text-xs text-[var(--color-text-light)] hover:text-[var(--color-gold)] transition-colors mt-1 inline-block"
                  data-testid="go-back-original-link"
                >
                  &larr; Go back to original
                </button></>
              )}
            </p>

            {/* Email + shipping + discount — always visible */}
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5 mb-4" data-testid="checkout-form">
              <div className="mb-4">
                <label className="font-body text-sm font-medium text-[var(--color-text-dark)] mb-1.5 flex items-center gap-1.5">
                  <Mail size={14} style={{ color: "var(--color-gold)" }} /> Email for delivery
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-[8px]"
                  data-testid="checkout-email-input"
                />
              </div>

                {/* Shipping address form — shown only when print is checked */}
                {includesPrint && (
                  <div className="mt-4 space-y-3 animate-slide-up" data-testid="shipping-form">
                    <p className="font-body text-xs font-medium text-[var(--color-text-dark)]">Shipping address</p>
                    <Input
                      value={shippingName}
                      onChange={(e) => setShippingName(e.target.value)}
                      placeholder="Full name *"
                      className="rounded-[8px]"
                      data-testid="shipping-name"
                    />
                    <Input
                      value={shippingLine1}
                      onChange={(e) => setShippingLine1(e.target.value)}
                      placeholder="Street address *"
                      className="rounded-[8px]"
                      data-testid="shipping-line1"
                    />
                    <Input
                      value={shippingLine2}
                      onChange={(e) => setShippingLine2(e.target.value)}
                      placeholder="Apt / Suite (optional)"
                      className="rounded-[8px]"
                      data-testid="shipping-line2"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={shippingCity}
                        onChange={(e) => setShippingCity(e.target.value)}
                        placeholder="City *"
                        className="rounded-[8px]"
                        data-testid="shipping-city"
                      />
                      <Input
                        value={shippingState}
                        onChange={(e) => setShippingState(e.target.value)}
                        placeholder="State *"
                        className="rounded-[8px]"
                        data-testid="shipping-state"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={shippingZip}
                        onChange={(e) => setShippingZip(e.target.value)}
                        placeholder="Zip code *"
                        className="rounded-[8px]"
                        data-testid="shipping-zip"
                      />
                      <select
                        value={shippingCountry}
                        onChange={(e) => setShippingCountry(e.target.value)}
                        className="w-full border border-[var(--color-border)] rounded-[8px] px-4 py-2 bg-[var(--color-white)] text-[var(--color-text-dark)] font-body text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-gold)]"
                        data-testid="shipping-country"
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Discount code */}
                <div className="mt-4 flex gap-2">
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder="Discount code"
                    className="rounded-[8px] flex-1"
                    disabled={!!discountApplied}
                    data-testid="discount-code-input"
                  />
                  {discountApplied ? (
                    <button onClick={() => { setDiscountApplied(""); setDiscountPercent(0); setDiscountCode(""); }} className="font-body text-xs px-3 py-2 rounded-[8px] border border-[var(--color-border)] text-[var(--color-text-mid)] hover:border-[var(--color-error)] transition-colors" data-testid="discount-remove-btn">Remove</button>
                  ) : (
                    <button onClick={applyDiscount} disabled={discountLoading || !discountCode.trim()} className="font-heading tracking-widest uppercase text-xs px-4 py-2 rounded-[8px] disabled:opacity-40" style={{ backgroundColor: "var(--color-navy)", color: "white" }} data-testid="discount-apply-btn">
                      {discountLoading ? "..." : "Apply"}
                    </button>
                  )}
                </div>
                {discountApplied && (
                  <p className="font-body text-xs mt-1" style={{ color: "var(--color-success)" }} data-testid="discount-applied-msg">
                    {discountApplied} &mdash; {discountPercent}% off applied
                  </p>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <span className="font-body text-xs text-[var(--color-text-light)]">
                    Powered by Stripe &middot; Secure checkout
                  </span>
                  <div className="text-right">
                    {discountPercent > 0 && (
                      <span className="font-body text-sm text-[var(--color-text-light)] line-through mr-2">${basePrice.toFixed(2)}</span>
                    )}
                    <span className="font-heading text-lg text-[var(--color-text-dark)]">${finalPrice}</span>
                  </div>
                </div>
              </div>

            {/* Trust signals */}
            <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5 space-y-3">
              <div className="flex items-center gap-3">
                <Download size={16} style={{ color: "var(--color-success)" }} />
                <span className="font-body text-sm text-[var(--color-text-mid)]">Your download will be watermark-free and full HD.</span>
              </div>
              <div className="flex items-center gap-3">
                <Lock size={16} style={{ color: "var(--color-success)" }} />
                <span className="font-body text-sm text-[var(--color-text-mid)]">Instant delivery to your email</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield size={16} style={{ color: "var(--color-success)" }} />
                <span className="font-body text-sm text-[var(--color-text-mid)]">Stripe Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
